import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BreatheView = ({ onComplete, voiceGuided }) => {
  // Countdown state
  const [countdownNumber, setCountdownNumber] = useState(5);
  const [countdownVisible, setCountdownVisible] = useState(true);
  const [isCountingDown, setIsCountingDown] = useState(true);
  const [showDarkness, setShowDarkness] = useState(false);
  
  // Exercise state
  const [exerciseStarted, setExerciseStarted] = useState(false);
  const [showCircle, setShowCircle] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [seconds, setSeconds] = useState(60);
  const [phase, setPhase] = useState('inhale');
  const [isComplete, setIsComplete] = useState(false);
  const [showFinalMessage, setShowFinalMessage] = useState(false);

  // Voice guidance refs
  const audioCtxRef = useRef(null);
  const cachedAudioRef = useRef({});
  const currentSourceRef = useRef(null);
  const mountedRef = useRef(true);

  const phaseText = {
    inhale: 'breathe in…',
    hold: 'hold…',
    exhale: 'breathe out…'
  };

  // ─── Audio helpers ───
  const ensureAudioCtx = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const fetchTTSAudio = useCallback(async (text) => {
    try {
      const response = await fetch(`${API}/tts/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, stability: 0.90, speed: 0.75 })
      });
      if (!response.ok) return null;
      const arrayBuffer = await response.arrayBuffer();
      const ctx = ensureAudioCtx();
      return await ctx.decodeAudioData(arrayBuffer);
    } catch (e) {
      console.error('Breathe TTS fetch failed:', e);
      return null;
    }
  }, [ensureAudioCtx]);

  const playAudioBuffer = useCallback((buffer) => {
    return new Promise((resolve) => {
      if (!buffer || !mountedRef.current) { resolve(); return; }
      try {
        const ctx = ensureAudioCtx();
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(1, ctx.currentTime + 0.2);
        source.connect(gain);
        gain.connect(ctx.destination);
        currentSourceRef.current = source;
        source.onended = () => {
          currentSourceRef.current = null;
          resolve();
        };
        source.start(0);
      } catch {
        resolve();
      }
    });
  }, [ensureAudioCtx]);

  const stopCurrentAudio = useCallback(() => {
    try { currentSourceRef.current?.stop(); } catch {}
    currentSourceRef.current = null;
  }, []);

  // ─── Pre-fetch all voice phrases on mount ───
  useEffect(() => {
    if (!voiceGuided) return;
    mountedRef.current = true;

    const prefetch = async () => {
      ensureAudioCtx();
      const phrases = {
        intro: "let's breathe together… beginning in five… four… three… two… one…",
        inhale: "breathe in…",
        hold: "hold…",
        exhale: "breathe out…",
        closing: "you're still here. and so am I.",
        ready: "whenever you're ready…"
      };

      const entries = Object.entries(phrases);
      const results = await Promise.all(
        entries.map(([, text]) => fetchTTSAudio(text))
      );
      if (!mountedRef.current) return;
      entries.forEach(([key], i) => {
        cachedAudioRef.current[key] = results[i];
      });
    };

    prefetch();

    return () => {
      mountedRef.current = false;
      stopCurrentAudio();
    };
  }, [voiceGuided, ensureAudioCtx, fetchTTSAudio, stopCurrentAudio]);

  // ─── Voice: play intro during countdown ───
  useEffect(() => {
    if (!voiceGuided || !isCountingDown) return;
    // Play the intro phrase as soon as countdown starts
    const t = setTimeout(() => {
      const buf = cachedAudioRef.current.intro;
      if (buf) playAudioBuffer(buf);
    }, 200);
    return () => clearTimeout(t);
  }, [voiceGuided, isCountingDown, playAudioBuffer]);

  // ─── Voice: speak phase changes during exercise ───
  useEffect(() => {
    if (!voiceGuided || !isActive) return;
    const buf = cachedAudioRef.current[phase];
    if (buf) playAudioBuffer(buf);
  }, [voiceGuided, isActive, phase, playAudioBuffer]);

  // ─── Voice: closing sequence ───
  useEffect(() => {
    if (!voiceGuided || !isComplete) return;
    const playClosing = async () => {
      const closing = cachedAudioRef.current.closing;
      if (closing) await playAudioBuffer(closing);
      if (!mountedRef.current) return;
      // 2 seconds of silence
      await new Promise(r => setTimeout(r, 2000));
      if (!mountedRef.current) return;
      const ready = cachedAudioRef.current.ready;
      if (ready) await playAudioBuffer(ready);
    };
    const t = setTimeout(playClosing, 600);
    return () => clearTimeout(t);
  }, [voiceGuided, isComplete, playAudioBuffer]);

  // Handle countdown sequence
  useEffect(() => {
    if (!isCountingDown) return;
    
    const countdownInterval = setInterval(() => {
      setCountdownVisible(false);
      
      setTimeout(() => {
        setCountdownNumber(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setIsCountingDown(false);
            setShowDarkness(true);
            return 0;
          }
          return prev - 1;
        });
        setCountdownVisible(true);
      }, 300);
      
    }, 1100);
    
    return () => clearInterval(countdownInterval);
  }, [isCountingDown]);

  // After countdown, show darkness then start exercise
  useEffect(() => {
    if (showDarkness && !exerciseStarted) {
      setTimeout(() => {
        setExerciseStarted(true);
        setTimeout(() => {
          setShowCircle(true);
          setIsActive(true);
        }, 100);
      }, 1000);
    }
  }, [showDarkness, exerciseStarted]);

  // Exercise countdown timer
  useEffect(() => {
    if (!isActive || seconds <= 0) return;
    
    const timer = setInterval(() => {
      setSeconds(s => s - 1);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isActive, seconds]);

  // Check for exercise completion
  useEffect(() => {
    if (seconds === 0 && !isComplete && exerciseStarted) {
      setIsComplete(true);
      setIsActive(false);
      stopCurrentAudio();
      
      setTimeout(() => {
        setShowFinalMessage(true);
      }, 500);
      
      // Longer delay for voice-guided mode to allow closing speech
      const delay = voiceGuided ? 12000 : 3500;
      setTimeout(() => {
        if (mountedRef.current) onComplete();
      }, delay);
    }
  }, [seconds, isComplete, onComplete, exerciseStarted, voiceGuided, stopCurrentAudio]);

  // Phase cycling (4s inhale, 4s hold, 4s exhale)
  useEffect(() => {
    if (!isActive) return;
    
    const phaseOrder = ['inhale', 'hold', 'exhale'];
    let currentIndex = 0;
    
    const phaseInterval = setInterval(() => {
      currentIndex = (currentIndex + 1) % 3;
      setPhase(phaseOrder[currentIndex]);
    }, 4000);
    
    return () => clearInterval(phaseInterval);
  }, [isActive]);

  const handleReturn = useCallback(() => {
    stopCurrentAudio();
    onComplete();
  }, [onComplete, stopCurrentAudio]);

  const handleCountdownCancel = () => {
    if (isCountingDown) {
      stopCurrentAudio();
      onComplete();
    }
  };

  const getCircleSize = () => {
    if (phase === 'inhale') return 200;
    if (phase === 'hold') return 200;
    return 80;
  };

  const getCircleFill = () => {
    if (phase === 'inhale' || phase === 'hold') {
      return 'rgba(196, 137, 74, 0.1)';
    }
    return 'rgba(196, 137, 74, 0.2)';
  };

  // Cleanup audio context on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopCurrentAudio();
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close().catch(() => {});
      }
    };
  }, [stopCurrentAudio]);

  return (
    <div 
      className="breathe-container"
      data-testid="breathe-view"
      onClick={isCountingDown ? handleCountdownCancel : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: '#080608',
        backgroundImage: exerciseStarted ? 'radial-gradient(ellipse at 50% 40%, rgba(62, 28, 18, 0.4) 0%, rgba(8, 6, 8, 0) 70%)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        cursor: isCountingDown ? 'pointer' : 'default'
      }}
    >
      {/* Velvet texture - grain */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.035,
          pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
        }}
      />
      
      {/* Vignette */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.7) 100%)'
        }}
      />

      {/* COUNTDOWN SEQUENCE */}
      {isCountingDown && (
        <>
          <div
            style={{
              position: 'relative',
              zIndex: 2
            }}
          >
            <span
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: '120px',
                fontWeight: 300,
                fontStyle: 'italic',
                color: 'rgba(255, 243, 225, 0.7)',
                opacity: countdownVisible ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
                display: 'block',
                lineHeight: 1
              }}
            >
              {countdownNumber}
            </span>
          </div>
          
          {/* Cancel option during countdown */}
          <p
            style={{
              position: 'absolute',
              bottom: '48px',
              left: '50%',
              transform: 'translateX(-50%)',
              fontFamily: "'Inter', sans-serif",
              fontSize: '13px',
              fontWeight: 400,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(255, 243, 225, 0.25)',
              zIndex: 2,
              margin: 0
            }}
          >
            tap anywhere to return
          </p>
        </>
      )}

      {/* DARKNESS PAUSE (between countdown and exercise) */}
      {showDarkness && !exerciseStarted && (
        <div style={{ position: 'relative', zIndex: 2 }} />
      )}

      {/* BREATHING EXERCISE */}
      {exerciseStarted && !isComplete && (
        <div 
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '48px',
            position: 'relative',
            zIndex: 2,
            opacity: showCircle ? 1 : 0,
            transition: 'opacity 2s ease-out'
          }}
        >
          {/* Breathing sphere */}
          <div
            className="breathing-sphere"
            style={{
              width: `${getCircleSize()}px`,
              height: `${getCircleSize()}px`,
              transition: 'all 4s ease-in-out',
            }}
          />
          
          {/* Phase text */}
          <p
            className="phase-text"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: '18px',
              fontWeight: 300,
              fontStyle: 'italic',
              color: 'rgba(255, 243, 225, 0.7)',
              letterSpacing: '0.05em',
              transition: 'opacity 0.5s ease',
              minHeight: '28px'
            }}
          >
            {phaseText[phase]}
          </p>
        </div>
      )}

      {/* COMPLETION MESSAGE */}
      {isComplete && (
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            opacity: showFinalMessage ? 1 : 0,
            transition: 'opacity 1s ease-out',
            textAlign: 'center'
          }}
        >
          <p
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: '24px',
              fontWeight: 300,
              fontStyle: 'italic',
              color: 'rgba(255, 243, 225, 0.82)',
              letterSpacing: '0.03em',
              marginBottom: voiceGuided ? '12px' : 0
            }}
          >
            You're still here.
          </p>
          {voiceGuided && (
            <p
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: '18px',
                fontWeight: 300,
                fontStyle: 'italic',
                color: 'rgba(255, 243, 225, 0.45)',
                letterSpacing: '0.03em',
                animation: 'breathe-text-fade-in 1.5s ease-out 1s forwards',
                opacity: 0
              }}
            >
              and so am I.
            </p>
          )}
        </div>
      )}

      {/* Return button during exercise */}
      {exerciseStarted && !isComplete && (
        <button
          onClick={handleReturn}
          data-testid="breathe-return-btn"
          style={{
            position: 'absolute',
            bottom: '48px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontFamily: "'Inter', sans-serif",
            fontSize: '12px',
            fontWeight: 400,
            letterSpacing: '0.1em',
            color: 'rgba(255, 243, 225, 0.2)',
            padding: '12px 24px',
            transition: 'color 0.3s ease',
            zIndex: 2,
            opacity: showCircle ? 1 : 0
          }}
          onMouseEnter={(e) => e.target.style.color = 'rgba(255, 243, 225, 0.35)'}
          onMouseLeave={(e) => e.target.style.color = 'rgba(255, 243, 225, 0.2)'}
        >
          return
        </button>
      )}

      {/* Animation keyframes */}
      <style>{`
        .breathing-circle {
          will-change: width, height, background;
        }
        
        .phase-text {
          animation: text-fade 4s ease-in-out infinite;
        }
        
        @keyframes text-fade {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        
        @keyframes breathe-text-fade-in {
          from { opacity: 0; }
          to { opacity: 0.45; }
        }
      `}</style>
    </div>
  );
};

export default BreatheView;
